import React, {useState} from 'react';
import {
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import {AdminPanelSettings} from "@mui/icons-material";
import {DateFormats, SubscriptionType, subscriptionTypeOptions} from "../constants/securspace-constants";
import moment, {Moment} from "moment";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {updateAccountAdmin} from "../user/request/user-requests";
import Busy from "../Busy";
import {useSnackbar} from "../../context/snackbar-context";
import {formatCurrencyValue, isInteger, parseCurrencyValue, validateCurrencyValue} from "../../util/PaymentUtils";
import {requestLoggedInAuthoritySource} from "../../services/session/session-requests";
import SettingsSection, {SettingsFieldGrid} from "./SettingsSection";
import {SelectChangeEvent} from "@mui/material/Select";
import {Account} from "../../types/Account";
import {AdminAccountUpdateRequest} from "../user/request/user-requests";

interface AdminInfoProps {
  account: Account;
  handleAccountUpdated: (account: Account) => void;
}

const AdminInfo = (props: AdminInfoProps) => {
  const {
    account,
    handleAccountUpdated
  } = props;
  const {snackbarShowMessage} = useSnackbar();

  // Fee percentages are held as strings while editing (text inputs); numeric API values are coerced on render.
  const [feePercentage, setFeePercentage] = useState<string | number>(account.feePercentage);
  const [subscriptionType, setSubscriptionType] = useState<string>(account.subscriptionType || SubscriptionType.MARKETPLACE_ONLY);
  const [startDate, setStartDate] = useState<string | null>(account.subscriptionEffectiveDate);
  const [subscriptionFee, setSubscriptionFee] = useState<number>(account.subscriptionFee);
  const [billingFeePercentage, setBillingFeePercentage] = useState<string | number>(account.billingFeePercentage);
  const [securspaceServiceFeePercentage, setSecurspaceServiceFeePercentage] = useState<string | number>(account.securspaceServiceFeePercentage);
  const [capacityEnforcementEnabled, setCapacityEnforcementEnabled] = useState(!!account.capacityEnforcementEnabled);
  const [changesMade, setChangesMade] = useState(false);

  const isGmsPro = subscriptionType === SubscriptionType.GMS_PRO;

  const valueInRange = (value: number, min: number, max: number): boolean => {
    return value >= min && value <= max;
  }

  const handleFeePercentageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length && (!isInteger(value) || !valueInRange(parseInt(value), 0, 100))) {
      return;
    }
    setChangesMade(true);
    setFeePercentage(value);
  };

  const handleSubscriptionTypeChange = (event: SelectChangeEvent) => {
    setChangesMade(true);
    setSubscriptionType(event.target.value);
  };

  const handleFromDateChange = (date: Moment | null) => {
    setChangesMade(true);
    if (date?.isValid()) {
      setStartDate(moment(date).format(DateFormats.DAY));
    } else {
      setStartDate(null);
    }
  };

  const handleSubscriptionFeeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (!validateCurrencyValue(value)) {
      return;
    }
    setChangesMade(true);
    setSubscriptionFee(parseCurrencyValue(value));
  };

  const handleServiceFeeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length && !valueInRange(parseFloat(value), 0, 100)) {
      return;
    }
    setChangesMade(true);
    setBillingFeePercentage(value);
  };

  const handleSubscriptionBillingFeeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value.length && !valueInRange(parseFloat(value), 0, 100)) {
      return;
    }
    setChangesMade(true);
    setSecurspaceServiceFeePercentage(event.target.value);
  };

  const handleCapacityEnforcementChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChangesMade(true);
    setCapacityEnforcementEnabled(event.target.checked);
  };

  const handleResponseError = (message: string, duration: number): void => {
    snackbarShowMessage(message, 'error', duration);
    Busy.set(false);
  }

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    Busy.set(true);

    const body: AdminAccountUpdateRequest = {
      feePercentage: Number(feePercentage),
      billingFeePercentage: Number(billingFeePercentage),
      securspaceServiceFeePercentage: Number(securspaceServiceFeePercentage),
      subscriptionType: subscriptionType,
      subscriptionFee: subscriptionFee,
      subscriptionEffectiveDate: moment(startDate, DateFormats.DAY).isValid() ? moment(startDate, DateFormats.DAY).format(DateFormats.DAY) : startDate,
      activated: account.activated,
      // Only sent when changed, so saving other fields never un-enrolls a supplier.
      ...(capacityEnforcementEnabled !== !!account.capacityEnforcementEnabled && {capacityEnforcementEnabled}),
    }

    updateAccountAdmin(account.id, body).then(() => {
      requestLoggedInAuthoritySource().then((resp) => {
        handleAccountUpdated(resp.body);
        snackbarShowMessage('Admin info updated successfully', 'success', 8000);
      }).catch(() => {
        handleResponseError('An error occurred while updating the Admin info', 10000);
      }).finally(() => {
        Busy.set(false);
        setChangesMade(false);
      });
    }).catch(() => {
      handleResponseError('An error occurred while updating the Admin info', 15000);
    });
  };

  return <form>
    <SettingsSection
      admin
      title={'SecurSpace Admin'}
      description={'Commercial terms for this account. Only SecurSpace admins see this section.'}
      icon={<AdminPanelSettings/>}
      actions={
        <Button
          type={'submit'}
          variant={'contained'}
          color={'secondary'}
          disabled={!changesMade}
          onClick={handleSubmit}
        >
          Save Admin Information
        </Button>
      }
    >
      <Paper variant={'outlined'} sx={{p: {xs: '1.14rem', sm: '1.71rem'}, display: 'flex', flexDirection: 'column', gap: '1.57rem'}}>
        <SettingsFieldGrid>
          <TextField
            variant='standard'
            value={feePercentage}
            label={'Marketplace Commission Fee %'}
            name={'feePercentage'}
            onChange={handleFeePercentageChange}
            placeholder={'Enter the SecurSpace Fee %'}
            fullWidth
          />
          <TextField
            variant='standard'
            value={billingFeePercentage}
            label={'Service Fee %'}
            name={'serviceFee'}
            onChange={handleServiceFeeChange}
            placeholder={'Enter the Service Fee %'}
            fullWidth
          />
        </SettingsFieldGrid>
        <Divider/>
        <SettingsFieldGrid>
          <FormControl variant="standard" fullWidth>
            <InputLabel id='ssSubscriptionTypeSelect'>Subscription Type</InputLabel>
            <Select
              value={subscriptionType}
              label="Subscription Type"
              onChange={handleSubscriptionTypeChange}
              labelId="ssSubscriptionTypeSelect"
            >
              {
                subscriptionTypeOptions?.map(item => (
                  <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                ))
              }
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label={'Subscription Start Date'}
              value={moment(startDate, DateFormats.DAY)}
              onChange={handleFromDateChange}
              format={DateFormats.DAY}
              slotProps={{textField: {variant: 'standard', fullWidth: true, error: false}}}
            />
          </LocalizationProvider>
          <TextField
            variant='standard'
            value={formatCurrencyValue(subscriptionFee)}
            label={'Subscription Fee'}
            name={'subscriptionFee'}
            onChange={handleSubscriptionFeeChange}
            placeholder={'Enter the Subscription Monthly Fee'}
            fullWidth
          />
          <TextField
            variant='standard'
            value={securspaceServiceFeePercentage}
            label={'Subscription Billing Fee %'}
            name={'securspaceServiceFeePercentage'}
            onChange={handleSubscriptionBillingFeeChange}
            placeholder={'Enter the Subscription Billing Fee %'}
            fullWidth
          />
        </SettingsFieldGrid>
        <Divider/>
        <FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={capacityEnforcementEnabled}
                onChange={handleCapacityEnforcementChange}
                disabled={!isGmsPro && !capacityEnforcementEnabled}
                name={'capacityEnforcementEnabled'}
              />
            }
            label={'Capacity Enforcement'}
          />
          <FormHelperText>
            {isGmsPro
              ? 'Hold this supplier\'s locations to their capacity: bookings over capacity are rejected and full locations show At Capacity. Applies only while capacity enforcement is switched on for the platform.'
              : 'Only GMS Pro suppliers can be enrolled.'}
          </FormHelperText>
        </FormControl>
      </Paper>
    </SettingsSection>
  </form>
}

export default AdminInfo;
